import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {DialogActions, DialogContent, Divider} from "@mui/material";
import {Row} from "reactstrap";
import Button from "../../../../base/button/button";
import DecreaseProductAvailabilityDialogSerializableSection from "./decrease";
import IncreaseProductAvailabilityDialogSerializableSection from "./increase";
import {formatMoney} from "../../../../../../core/services/utils/utils";
import Api from "../../../../../../core/services/api/api";

const formId = 'reason-note-form-id'

const ModifyProductAvailabilityDialogSerializableSection = ({
                                                                alreadySelectedSerials,
                                                                handleLocalUpdate,
                                                                increase,
                                                                partNo,
                                                                currentAvailability,
                                                                close,
                                                                setNewAvailability,
                                                                keyInformation,
                                                                alreadyAppliedCost,
                                                            }) => {
    const [modifying, setModifying] = useState(false);
    const [removedSerials, setRemovedSerials] = useState([]);
    const [addedSerials, setAddedSerials] = useState([]);
    const [showDecreaseFinalStep, setShowDecreaseFinalStep] = useState(false);
    const [cost, setCost] = useState('');
    const isMounted = useIsMounted();

    /**
     * With each change in the [alreadySelectedSerials] prop, sets the added serials or removed serials based on the [increase] prop
     */
    useEffect(() => {
        if (increase) {
            setAddedSerials(alreadySelectedSerials ?? [])
            setCost(alreadyAppliedCost?.toString() ?? '');
            return;
        }
        setRemovedSerials(alreadySelectedSerials ?? [])
    }, [alreadySelectedSerials, increase])

    /**@type {boolean}*/
    const canModify = useMemo(() => {
        if (increase) {
            return !!cost.length && !Number.isNaN(Number(cost)) && (Number(cost) >= 0) && !!addedSerials.length;
        } else {
            return !!removedSerials.length
        }
    }, [addedSerials.length, cost, increase, removedSerials.length])

    /**
     * Modifies the availability of the provided serialized product in the provided warehouse.
     * @type {(function(values): Promise<void>)}
     */
    const modifyProductAvailability = useCallback(async (values) => {
        if (!canModify)
            return;
        if (!!handleLocalUpdate) {
            handleLocalUpdate(increase, increase ? addedSerials : removedSerials, Number(cost))
            close();
            return;
        }
        setModifying(true);
        const forApi = {
            items: (increase ? addedSerials : removedSerials).map(serialNo => ({
                partNumber: partNo,
                partId: keyInformation.id,
                parentCategoryId: keyInformation.parentCategoryId,
                parentCategoryTitle: keyInformation.parentCategory,
                serialNumber: increase ? serialNo : serialNo.serialNo,
                cost: increase ? Number(cost) : serialNo.cost,
                quantity: 1,
                warehouseCode: keyInformation.warehouse,
                reasonId: values?.reason?.id ?? null,
                note: values?.note ?? null,
            })),
            isAdd: increase,
        }
        const response = await Api.createInventoryAdjustment(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            close();
            setNewAvailability(currentAvailability + (increase ? addedSerials.length : -(removedSerials.length)));
        }
        setModifying(false);
    }, [addedSerials, canModify, close, cost, currentAvailability, handleLocalUpdate, increase, isMounted, keyInformation.id, keyInformation.parentCategory, keyInformation.parentCategoryId, keyInformation.warehouse, partNo, removedSerials, setNewAvailability])

    /**
     * Callback function passed to the bottom section form to be called when the form is submitted
     * Passes the values of the form for the function for modifying the availabilities
     * @param values
     */
    const submitReasonNote = (values) => {
        modifyProductAvailability(values).then();
    }

    const toggleShowingLastSection = (e) => {
        e.preventDefault();
        setShowDecreaseFinalStep(prevState => !prevState);
    }


    return (
        <>
            <DialogContent className={'modify-product-availability-dialog-content'}>
                {
                    increase === true &&
                    <IncreaseProductAvailabilityDialogSerializableSection
                        currentAvailability={currentAvailability}
                        selectedSerialNos={addedSerials}
                        setSelectedSerialNos={setAddedSerials}
                        modifying={modifying}
                        cost={cost}
                        setCost={setCost}
                        formId={formId}
                        submitting={modifying}
                        submitReasonNote={submitReasonNote}
                        hideReasonNoteSection={!!handleLocalUpdate}
                    />
                }
                {
                    increase === false &&
                    <DecreaseProductAvailabilityDialogSerializableSection
                        partNo={partNo}
                        currentAvailability={currentAvailability}
                        selectedSerialNos={removedSerials}
                        setSelectedSerialNos={setRemovedSerials}
                        modifying={modifying}
                        formId={formId}
                        showLastSection={showDecreaseFinalStep}
                        onSubmitReasonNotesForm={submitReasonNote}
                        hideReasonNoteSection={!!handleLocalUpdate}
                        keyInformation={keyInformation}
                    />
                }
            </DialogContent>
            <DialogActions className={'p-0 d-flex flex-column'}>
                <Divider className='divider'/>
                <Row className='no-gutters justify-content-between align-items-start w-100 m-0'>
                    {
                        !showDecreaseFinalStep && increase === false
                            ? <div className={'remove-serialized-availability-serial-selection-footer'}>
                                <div>
                                    <p className={'identifier'}>
                                        {`${removedSerials.length} item${removedSerials?.length > 1 ? 's' : ''} selected`}
                                    </p>
                                    <p className={'extended-cost'}>
                                        Extended Cost: <span
                                        className={'extended-cost-amount'}>
                                    {`${formatMoney(-removedSerials.reduce((prev, current) => prev + (current.cost ?? 0), 0))}`}
                                </span>
                                    </p>
                                </div>
                                <Button
                                    disabled={modifying || !canModify}
                                    type={"button"}
                                    variant={'contained'}
                                    color={'primary'}
                                    className={'px-5'}
                                    onClick={toggleShowingLastSection}
                                >
                                    Next
                                </Button>
                            </div>
                            : <>
                                <Button
                                    disabled={modifying}
                                    variant={'dialog'}
                                    color={'primary'}
                                    type={"button"}
                                    onClick={increase === false ? toggleShowingLastSection : close}
                                    borderRadius={'0 8px 0 8px'}
                                >
                                    {increase === false ? 'Back' : 'Close'}
                                </Button>
                                <Button
                                    disabled={modifying || !canModify}
                                    variant={'dialog'}
                                    color={'primary'}
                                    borderRadius={'8px 0 8px 0'}
                                    {...(
                                        !!handleLocalUpdate
                                            ? {type: 'button', onClick: modifyProductAvailability}
                                            : {type: "submit", form: formId}
                                    )
                                    }
                                >
                                    {modifying ? "Processing..." : "Finalize"}
                                </Button>
                            </>
                    }
                </Row>
            </DialogActions>
        </>
    );
}
export default ModifyProductAvailabilityDialogSerializableSection;
